import type { BacklogTaskResult, UpdateBacklogIssuePayload } from '@ai-meeting/shared'
import type { UiStrings } from '@/constants/ui-locale'

/**
 * The ticket as it stands, flattened out of whichever reply carried it.
 *
 * `POST /tasks` reports all of this for a matched row, and so does the reply
 * to a refused `PATCH`. Taking the same shape from both is what lets the panel
 * re-render on a refusal without a second read — and it is why the panel opens
 * with no round trip at all.
 */
export interface ConfirmTicket {
  issueKey: string
  url: string | null
  summary: string
  priority: string | null
  assigneeId: number | null
  assigneeName: string | null
  dueDate: string | null
  /**
   * Three values, three different things. A string is the description, `''`
   * means the ticket has none, and `null` means it could not be read — the
   * last of which must not be shown as an empty ticket.
   */
  description: string | null
  /** Echoed back as `confirmed_updated`; the server refuses if it has moved. */
  updated: string | null
  /** false → these came from a cached snapshot, so say so before overwriting. */
  isFresh: boolean
}

/**
 * Whether this row would overwrite a ticket that still exists unchanged.
 *
 * Every such row is held out of the batch and sent through the confirmation
 * panel instead, which is not how `POST /tasks` behaves on its own: a
 * confident match comes back `updated`, meaning the batch overwrote a ticket
 * without anybody being shown what was on it. Backlog has no delete API, so
 * that is not something to find out afterwards.
 *
 * A low-confidence match already stops at `matched`. This makes the two
 * behave the same, so "Update" on this table always means "nothing has been
 * written yet".
 */
export function needsConfirmation(row: BacklogTaskResult | undefined): boolean {
  if (!row) return false
  // Already settled, one way or the other.
  if (row.status === 'created' || row.status === 'updated' || row.status === 'failed') return false
  if (row.action_type !== 'update') return false
  return Boolean(row.matched_issue_key ?? row.issue_key)
}

/**
 * Whether the server settled this row by finding the issue it would have made.
 *
 * `POST /tasks` stamps every issue it creates with a marker built from the
 * meeting id and the title, and looks that marker up before creating anything,
 * so a second press comes back `skipped_duplicate` instead of a second ticket.
 * The row therefore carries a ticket key and has nothing left to do — which is
 * neither "about to be created" nor "still waiting", the two things the table
 * used to say about it.
 *
 * @param row The verdict for this action item.
 */
export function isAlreadyCreated(row: BacklogTaskResult | undefined): boolean {
  return row?.status === 'skipped_duplicate'
}

/**
 * Roughly how many lines a description will take in the panel's left column.
/**
 * Roughly how many lines a description will take in the panel's left column.
 *
 * Counted rather than measured, because measuring means laying the text out
 * and reading `scrollHeight`, which is zero in a test — the expander would be
 * impossible to cover. Counted by *line* rather than by total length, because
 * a newline costs a whole line however short it is: the usual first line here
 * is `Nội dung công việc:`, nineteen characters that fill one.
 *
 * `CHARS_PER_LINE` was taken from a browser: at this column's measured width
 * and `text-sm`, the sample from the design wrapped to exactly the four lines
 * this returns for it.
 *
 * @param text The description as the ticket has it.
 * @returns Lines, never fewer than one.
 */
export function estimateDescriptionLines(text: string): number {
  const CHARS_PER_LINE = 50
  return text
    .split('\n')
    .reduce((lines, line) => lines + Math.max(1, Math.ceil(line.length / CHARS_PER_LINE)), 0)
}

/** Read a matched row's view of the ticket it would overwrite. */
export function ticketFromRow(row: BacklogTaskResult): ConfirmTicket | null {
  const issueKey = row.matched_issue_key ?? row.issue_key
  if (!issueKey) return null
  return {
    issueKey,
    url: row.url,
    summary: row.current_summary ?? row.title,
    priority: row.current_priority,
    assigneeId: row.current_assignee_id,
    assigneeName: row.current_assignee_name,
    dueDate: row.current_deadline,
    // Normalised, because a missing field and an explicit null mean the same
    // thing here — the description could not be read — and the two have to
    // behave the same. An older AI service sends no field at all, and the
    // panel branches on the value rather than on its presence.
    description: row.current_description ?? null,
    updated: row.current_updated,
    // Only an explicit false means stale. Missing is not a claim either way,
    // and warning on every row an older service answered is noise.
    isFresh: row.current_is_fresh !== false,
  }
}

/**
 * The fields a reviewer can change before agreeing to overwrite a ticket.
 */
export interface ConfirmDraft {
  summary: string
  priority: string
  assigneeId: number | null
  dueDate: string
  description: string
  /** True once the description box has been edited, which is what sends it. */
  descriptionTouched: boolean
}

/**
 * What to offer when the project's own priority names could not be read.
 *
 * Backlog ships these four and most projects keep them, but a project is free
 * to rename them — which is why this is the fallback and `project-meta` is the
 * source. Writing a name the project does not define is refused with 422.
 */
export const FALLBACK_PRIORITIES = ['High', 'Normal', 'Low'] as const

/**
 * The draft as the panel opens: the action item's own wording, against the
 * ticket's current values for everything the minutes do not carry.
 *
 * Priority seeds from the ticket rather than from a default, so one somebody
 * deliberately raised to High is not quietly dropped back to Normal by a
 * reviewer who never looked at that row.
 *
 * The description seeds from the ticket too, not from the action item: this
 * box is the ticket's description, and pre-filling it with the minutes would
 * propose replacing the whole body with a one-line action item nobody asked to
 * write. An unreadable description (`null`) starts empty and stays unsent
 * until somebody types.
 *
 * @param ticket The ticket as the row reported it.
 * @param proposed What the sync would have written: the action item's content,
 *   the member resolved for it, and its deadline.
 */
export function initialDraft(
  ticket: ConfirmTicket,
  proposed: { summary: string; assigneeId: number | null; dueDate: string }
): ConfirmDraft {
  return {
    summary: proposed.summary.trim() || ticket.summary,
    priority: ticket.priority ?? 'Normal',
    assigneeId: proposed.assigneeId ?? ticket.assigneeId,
    dueDate: proposed.dueDate || (ticket.dueDate ?? ''),
    description: ticket.description ?? '',
    descriptionTouched: false,
  }
}

/**
 * Only what the reviewer actually changed.
 *
 * Built by comparing against the ticket rather than by sending the whole
 * draft: every field present is a field the AI service writes, and writing a
 * value back over the identical value still counts as an edit in Backlog's
 * own history.
 *
 * @param draft What is on screen when Confirm is pressed.
 * @param ticket The ticket as the panel read it, including the `updated` stamp
 *   that makes the write refusable if it has moved on since.
 */
export function buildUpdatePayload(
  draft: ConfirmDraft,
  ticket: ConfirmTicket
): UpdateBacklogIssuePayload {
  const payload: UpdateBacklogIssuePayload = { confirmed_updated: ticket.updated }
  if (draft.summary.trim() && draft.summary.trim() !== ticket.summary) {
    payload.summary = draft.summary.trim()
  }
  if (draft.priority && draft.priority !== ticket.priority) payload.priority = draft.priority
  if (draft.assigneeId !== ticket.assigneeId) payload.assignee_id = draft.assigneeId
  if (draft.dueDate !== (ticket.dueDate ?? '')) payload.due_date = draft.dueDate || null
  if (draft.descriptionTouched) {
    payload.description = draft.description
    // Still sent although the route now treats an absent field as untouched:
    // the flag is still in the upstream signature, and if it is the thing that
    // gates the write then dropping it loses descriptions silently.
    payload.write_description = true
  }
  return payload
}

/** A field the confirmation panel is able to write. */
export type WritableField = 'summary' | 'description' | 'priority' | 'assignee_id' | 'due_date'

/**
 * What `changed_fields` calls each of the fields this panel can write.
 *
 * The AI service names the fields it wrote, and nothing in this repo fixes
 * that vocabulary — the only evidence is a `['summary', 'assignee',
 * 'due_date']` fixture, which already shows the names are not the payload's
 * own (`assignee`, not `assignee_id`). So each field carries every spelling it
 * could plausibly come back as, matched with case and punctuation removed.
 * When the AI service states the real list, this is the single place to fix.
 */
const CHANGED_FIELD_ALIASES: Record<WritableField, string[]> = {
  summary: ['summary', 'subject', 'title'],
  description: ['description', 'body'],
  priority: ['priority'],
  assignee_id: ['assignee', 'assignee_id', 'assignee_name'],
  due_date: ['due_date', 'deadline', 'limit_date'],
}

/** Letters and digits only, so `due_date`, `dueDate` and `DueDate` are one name. */
function normaliseFieldName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * The fields a confirmed write asked for that the reply did not report writing.
 *
 * Backlog has no way to tell the reviewer that part of their edit was dropped:
 * the reply carries `changed_fields`, and until BRAI-779 nothing read it, so a
 * field the AI service silently skipped looked exactly like one it wrote.
 *
 * Returns nothing when `changedFields` is empty. An empty list says nothing
 * about any single field, and a service that never fills it in would otherwise
 * make every confirmed write look half-failed — the conclusion is only drawn
 * against a list that demonstrably has entries. `status: 'noop'` already covers
 * the case where nothing at all was written.
 *
 * @param payload What was sent, so that only fields actually asked for are judged.
 * @param changedFields `changed_fields` as the reply gave it.
 * @returns The unreported fields, in the order the panel lists them.
 */
export function unreportedFields(
  payload: UpdateBacklogIssuePayload,
  changedFields: string[]
): WritableField[] {
  if (changedFields.length === 0) return []
  const reported = new Set(changedFields.map(normaliseFieldName))
  const asked: WritableField[] = []
  if (payload.summary !== undefined) asked.push('summary')
  if (payload.description !== undefined) asked.push('description')
  if (payload.priority !== undefined) asked.push('priority')
  if (payload.assignee_id !== undefined) asked.push('assignee_id')
  if (payload.due_date !== undefined) asked.push('due_date')
  return asked.filter(
    (field) =>
      !CHANGED_FIELD_ALIASES[field].some((alias) => reported.has(normaliseFieldName(alias)))
  )
}

/**
 * The panel's own label for a field, so a message about it reads the same as
 * the row the reviewer was editing rather than naming the wire field.
 */
export function backlogFieldLabel(field: WritableField, t: UiStrings): string {
  switch (field) {
    case 'summary':
      return t.backlogFieldSummary
    case 'description':
      return t.backlogFieldDescription
    case 'priority':
      return t.backlogFieldPriority
    case 'assignee_id':
      return t.backlogFieldAssignee
    case 'due_date':
      return t.backlogFieldDueDate
  }
}

/**
 * Whether confirming this row exactly as the sync proposes it would write
 * nothing at all.
 *
 * Asked of the payload the panel would actually send, not by comparing the
 * fields a second time: a separate comparison is free to drift from the one
 * that decides the write, and then the table and the ticket disagree. Only
 * `confirmed_updated` survives a write that touches nothing — it is the
 * staleness guard rather than a field.
 *
 * @param ticket The ticket as the row reported it.
 * @param proposed What the sync would write: the action item's content, the
 *   member resolved for it, and its deadline.
 */
export function wouldWriteNothing(
  ticket: ConfirmTicket,
  proposed: { summary: string; assigneeId: number | null; dueDate: string }
): boolean {
  const payload = buildUpdatePayload(initialDraft(ticket, proposed), ticket)
  return Object.keys(payload).every((field) => field === 'confirmed_updated')
}

/**
 * Whether this row still has to be confirmed before anything can be pushed.
 *
 * {@link needsConfirmation} answers that for the batch, where every matched
 * overwrite is held back whatever it contains. This is the reviewer's version
 * of the question, and it has one more answer: a row the sync would write
 * nothing to has nothing to confirm, so asking again is asking about an edit
 * that does not exist.
 *
 * That is what BRAI-806 reported — a panel reopening with every field reading
 * the same on both sides. Confirming a row writes those values back into the
 * minutes (BRAI-779), which makes the two match, and nothing then used that to
 * stop asking.
 *
 * Deliberately NOT used to decide what the batch sends: `POST /tasks` writes a
 * confident match itself, and writing the same value back still counts as an
 * edit in the ticket's history.
 */
export function confirmNeeded(
  row: BacklogTaskResult | undefined,
  proposed: { summary: string; assigneeId: number | null; dueDate: string }
): boolean {
  if (!needsConfirmation(row)) return false
  const ticket = ticketFromRow(row!)
  if (!ticket) return false
  return !wouldWriteNothing(ticket, proposed)
}
